import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Monitor, Globe, TrendingUp, TrendingDown, Minus, Clock, ChevronLeft, ChevronDown, ChevronRight, Users, List, BarChart3, Layers, Play, Square, WifiOff, Pause, Coffee, Zap } from 'lucide-react';
import { PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

function formatDuration(seconds) {
  if (!seconds || seconds === 0) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

const COLORS = {
  productive: '#10b981',
  unproductive: '#f43f5e',
  neutral: '#64748b',
  uncategorized: '#a1a1aa'
};

const PIE_COLORS = ['#10b981', '#f43f5e', '#64748b', '#a1a1aa'];

export default function UsageDashboard() {
  const { user } = useAuth();
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [summary, setSummary] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [teamOverview, setTeamOverview] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [workSessions, setWorkSessions] = useState(null);
  const [detailTab, setDetailTab] = useState('summary');
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('inspirotracker_token');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const userParam = selectedUser ? `&user_id=${selectedUser.user_id}` : '';

      const [sumRes, breakRes, teamRes] = await Promise.all([
        fetch(`${API}/api/usage/summary?date=${date}${userParam}`, { headers }),
        fetch(`${API}/api/usage/breakdown?date=${date}${userParam}`, { headers }),
        !selectedUser ? fetch(`${API}/api/usage/team-overview?date=${date}`, { headers }) : Promise.resolve(null)
      ]);

      const sumData = await sumRes.json();
      const breakData = await breakRes.json();
      setSummary(sumData);
      setBreakdown(Array.isArray(breakData) ? breakData : []);
      if (teamRes) {
        const teamData = await teamRes.json();
        setTeamOverview(Array.isArray(teamData) ? teamData : []);
      }

      // Fetch timeline and details if a user is selected
      if (selectedUser) {
        const [tlRes, dtRes, alRes, wsRes] = await Promise.all([
          fetch(`${API}/api/usage/timeline?date=${date}&user_id=${selectedUser.user_id}`, { headers }),
          fetch(`${API}/api/usage/user-details?date=${date}&user_id=${selectedUser.user_id}`, { headers }),
          fetch(`${API}/api/usage/activity-log?date=${date}&user_id=${selectedUser.user_id}`, { headers }),
          fetch(`${API}/api/usage/work-sessions?date=${date}&user_id=${selectedUser.user_id}`, { headers })
        ]);
        const tlData = await tlRes.json();
        setTimeline(Array.isArray(tlData) ? tlData : []);
        const dtData = await dtRes.json();
        setUserDetails(Array.isArray(dtData) ? dtData : []);
        const alData = await alRes.json();
        setActivityLog(Array.isArray(alData) ? alData : []);
        const wsData = await wsRes.json();
        setWorkSessions(wsData && wsData.sessions ? wsData : null);
      } else {
        setTimeline([]);
        setUserDetails([]);
        setActivityLog([]);
        setWorkSessions(null);
      }
    } catch (err) {
      console.error('Failed to fetch usage data:', err);
    } finally {
      setLoading(false);
    }
  }, [date, selectedUser, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const pieData = breakdown
    .filter(b => b.seconds > 0)
    .map(b => ({
      name: b.category.charAt(0).toUpperCase() + b.category.slice(1),
      value: b.seconds,
      percentage: b.percentage,
      fill: COLORS[b.category] || COLORS.uncategorized
    }));

  const timelineData = timeline.map(t => ({
    hour: `${t.hour}:00`,
    Productive: Math.round(t.productive / 60),
    Unproductive: Math.round(t.unproductive / 60),
    Neutral: Math.round(t.neutral / 60)
  }));

  const handleDateChange = (offset) => {
    const d = new Date(date);
    d.setDate(d.getDate() + offset);
    setDate(d.toISOString().split('T')[0]);
  };

  if (!['admin', 'manager'].includes(user?.role)) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full p-8">
          <p className="text-slate-500 dark:text-slate-400">Access restricted to admins and managers.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="space-y-6 p-1" data-testid="usage-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {selectedUser && (
            <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)} data-testid="back-to-team-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> Team
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="usage-dashboard-title">
              <Monitor className="w-6 h-6 inline-block mr-2 mb-1" />
              {selectedUser ? `${selectedUser.user_name}'s Activity` : 'Usage Tracking'}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {selectedUser ? 'Detailed activity breakdown' : 'Team productivity overview'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => handleDateChange(-1)} data-testid="prev-date-btn">&larr;</Button>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            data-testid="date-picker"
          />
          <Button variant="outline" size="sm" onClick={() => handleDateChange(1)} data-testid="next-date-btn">&rarr;</Button>
          <Button variant="outline" size="sm" onClick={() => setDate(new Date().toISOString().split('T')[0])} data-testid="today-btn">Today</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {/* Summary Cards — reflect selected employee when one is picked, otherwise team-wide */}
          <div className="space-y-2">
            {selectedUser && (
              <p className="text-sm text-slate-600 dark:text-slate-400" data-testid="summary-cards-scope">
                Showing <span className="font-semibold text-slate-800 dark:text-slate-200">{selectedUser.user_name}</span>'s activity ·{' '}
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-indigo-600 dark:text-indigo-400 hover:underline"
                  data-testid="clear-selected-user-btn"
                >
                  show team totals
                </button>
              </p>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <SummaryCard
                title="Productive"
                seconds={selectedUser ? selectedUser.productive_seconds : (summary?.productive_seconds || 0)}
                total={selectedUser ? selectedUser.total_seconds : (summary?.total_seconds || 1)}
                color="emerald"
                icon={<TrendingUp className="w-5 h-5" />}
              />
              <SummaryCard
                title="Unproductive"
                seconds={selectedUser ? selectedUser.unproductive_seconds : (summary?.unproductive_seconds || 0)}
                total={selectedUser ? selectedUser.total_seconds : (summary?.total_seconds || 1)}
                color="rose"
                icon={<TrendingDown className="w-5 h-5" />}
              />
              <SummaryCard
                title="Neutral"
                seconds={selectedUser ? selectedUser.neutral_seconds : (summary?.neutral_seconds || 0)}
                total={selectedUser ? selectedUser.total_seconds : (summary?.total_seconds || 1)}
                color="slate"
                icon={<Minus className="w-5 h-5" />}
              />
              <SummaryCard
                title="Uncategorized"
                seconds={selectedUser ? (selectedUser.uncategorized_seconds || 0) : (summary?.uncategorized_seconds || 0)}
                total={selectedUser ? selectedUser.total_seconds : (summary?.total_seconds || 1)}
                color="amber"
                icon={<Layers className="w-5 h-5" />}
              />
              <SummaryCard
                title="Total Tracked"
                seconds={selectedUser ? selectedUser.total_seconds : (summary?.total_seconds || 0)}
                total={0}
                color="indigo"
                icon={<Clock className="w-5 h-5" />}
                isTotal
              />
            </div>
          </div>

          {/* Charts Row */}
          <div className={`grid grid-cols-1 ${selectedUser ? 'lg:grid-cols-2' : ''} gap-6`}>
            {/* Donut Chart */}
            <Card className="border border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-slate-800 dark:text-slate-200">Category Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                {pieData.length > 0 ? (
                  <div className="flex items-center justify-center">
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={65}
                          outerRadius={100}
                          paddingAngle={3}
                          dataKey="value"
                          stroke="none"
                        >
                          {pieData.map((entry, idx) => (
                            <Cell key={idx} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [formatDuration(value), name]}
                          contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }}
                        />
                        <Legend
                          formatter={(value) => <span className="text-sm text-slate-600 dark:text-slate-300">{value}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[260px] text-slate-400">No data for this date</div>
                )}
              </CardContent>
            </Card>

            {/* Timeline Chart — only shown when an employee is selected */}
            {selectedUser && (
              <Card className="border border-slate-200 dark:border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-slate-800 dark:text-slate-200">
                    Hour-by-Hour Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {timelineData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <AreaChart data={timelineData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
                        <XAxis dataKey="hour" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                        <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" label={{ value: 'minutes', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#94a3b8' } }} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }}
                          formatter={(value) => [`${value} min`]}
                        />
                        <Area type="monotone" dataKey="Productive" stackId="1" fill="#10b981" stroke="#10b981" fillOpacity={0.6} />
                        <Area type="monotone" dataKey="Unproductive" stackId="1" fill="#f43f5e" stroke="#f43f5e" fillOpacity={0.6} />
                        <Area type="monotone" dataKey="Neutral" stackId="1" fill="#64748b" stroke="#64748b" fillOpacity={0.4} />
                        <Legend />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[260px] text-slate-400 gap-2">
                      <Users className="w-8 h-8 opacity-50" />
                      <p className="text-sm">No timeline data</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Team Overview Table */}
          {!selectedUser && (
            <Card className="border border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-slate-800 dark:text-slate-200">Team Overview</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {teamOverview.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-200 dark:border-slate-700">
                        <TableHead className="text-xs font-semibold uppercase tracking-wider">Employee</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider">Department</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-center">Productive</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-center">Unproductive</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-center">Neutral</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-center">Uncategorized</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {teamOverview
                        .sort((a, b) => b.productive_pct - a.productive_pct)
                        .map((row) => (
                          <TableRow
                            key={row.user_id}
                            className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 border-slate-200 dark:border-slate-700"
                            onClick={() => setSelectedUser(row)}
                            data-testid={`team-row-${row.user_id}`}
                          >
                            <TableCell className="font-medium text-slate-900 dark:text-slate-100">{row.user_name}</TableCell>
                            <TableCell className="text-slate-500 dark:text-slate-400 text-sm">{row.department || '—'}</TableCell>
                            <TableCell className="text-center">
                              <span className="inline-flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">{row.productive_pct}%</span>
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="inline-flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-rose-500" />
                                <span className="text-sm font-medium text-rose-600 dark:text-rose-400">{row.unproductive_pct}%</span>
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="inline-flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-slate-400" />
                                <span className="text-sm font-medium text-slate-500 dark:text-slate-400">{row.neutral_pct}%</span>
                              </span>
                            </TableCell>
                            <TableCell className="text-center" data-testid={`team-row-${row.user_id}-uncategorized`}>
                              <span className="inline-flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-amber-500" />
                                <span className="text-sm font-medium text-amber-600 dark:text-amber-400">{row.uncategorized_pct ?? 0}%</span>
                              </span>
                            </TableCell>
                            <TableCell className="text-right text-sm font-medium text-slate-700 dark:text-slate-300">
                              {formatDuration(row.total_seconds)}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="flex items-center justify-center py-12 text-slate-400">
                    <p>No tracking data for this date</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Individual User Detail (cards moved to top KPI row) */}

          {/* Websites & Apps Detail Table — Tabbed View */}
          {selectedUser && (
            <Card className="border border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold text-slate-800 dark:text-slate-200">
                    {detailTab === 'summary' ? 'Websites & Apps Used' : detailTab === 'timeline' ? 'Activity Timeline' : 'Work Sessions'} — {selectedUser.user_name}
                  </CardTitle>
                  <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
                    <button
                      onClick={() => setDetailTab('summary')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                        detailTab === 'summary'
                          ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                      }`}
                      data-testid="tab-summary"
                    >
                      <BarChart3 className="w-3.5 h-3.5" /> Summary
                    </button>
                    <button
                      onClick={() => setDetailTab('timeline')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                        detailTab === 'timeline'
                          ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                      }`}
                      data-testid="tab-timeline"
                    >
                      <List className="w-3.5 h-3.5" /> Timeline
                    </button>
                    <button
                      onClick={() => setDetailTab('sessions')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                        detailTab === 'sessions'
                          ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                      }`}
                      data-testid="tab-sessions"
                    >
                      <Layers className="w-3.5 h-3.5" /> Sessions
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {detailTab === 'summary' ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-200 dark:border-slate-700">
                        <TableHead className="text-xs font-semibold uppercase tracking-wider w-10">Type</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider">Name</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-center">Category</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Duration</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {userDetails.map((item, idx) => (
                        <UsageDetailRow key={idx} item={item} idx={idx} />
                      ))}
                    </TableBody>
                  </Table>
                ) : detailTab === 'timeline' ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-200 dark:border-slate-700">
                        <TableHead className="text-xs font-semibold uppercase tracking-wider w-24">Time</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider w-10">Type</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider">App / Website</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-center">Category</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Duration</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activityLog.map((entry, idx) => {
                        const startTime = new Date(entry.started_at);
                        const endTime = new Date(entry.ended_at);
                        const timeStr = `${startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                        const displayName = entry.domain || entry.app_name;
                        const type = entry.domain ? 'domain' : 'app';
                        const catStyle = getCategoryStyle(entry.category);

                        return (
                          <TableRow key={idx} className="border-slate-200 dark:border-slate-700" data-testid={`timeline-row-${idx}`}>
                            <TableCell className="text-xs font-mono text-slate-500 dark:text-slate-400 whitespace-nowrap">
                              {timeStr}
                            </TableCell>
                            <TableCell>
                              {type === 'domain' ? (
                                <Globe className="w-4 h-4 text-blue-500" />
                              ) : (
                                <Monitor className="w-4 h-4 text-purple-500" />
                              )}
                            </TableCell>
                            <TableCell className="font-medium text-slate-900 dark:text-slate-100">
                              <div className="flex flex-col">
                                <span>{displayName}</span>
                                {entry.window_title && entry.window_title !== displayName && (
                                  <span
                                    className="text-xs text-slate-500 dark:text-slate-400 font-normal truncate max-w-md"
                                    title={entry.window_title}
                                    data-testid={`timeline-row-${idx}-title`}
                                  >
                                    {entry.window_title}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${catStyle.bg} ${catStyle.text}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${catStyle.dot}`} />
                                {catStyle.label}
                              </span>
                            </TableCell>
                            <TableCell className="text-right text-sm font-medium text-slate-700 dark:text-slate-300">
                              {formatDuration(entry.duration_seconds)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {activityLog.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-slate-400">
                            No chronological data available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                ) : (
                  /* Sessions Tab */
                  <div data-testid="sessions-tab-content">
                    {workSessions && workSessions.totals ? (
                      <>
                        {/* Clarification hint — Sessions ≠ Total Tracked */}
                        <div className="px-4 pt-3 pb-1 text-xs text-slate-500 dark:text-slate-400 leading-relaxed" data-testid="sessions-tip">
                          <span className="font-medium text-slate-600 dark:text-slate-300">Sessions</span> show explicit clock-in/clock-out periods (manual timer or auto clock-in in stealth mode).
                          They are based on <span className="font-medium">time entries</span>, while <span className="font-medium">Total Tracked</span> in the top cards counts every app/window captured by the desktop agent.
                          The two may differ if the agent ran outside any timer session.
                        </div>
                        {/* Session Totals Bar */}
                        <div className="grid grid-cols-3 gap-3 p-4 border-b border-slate-200 dark:border-slate-700">
                          <div className="flex items-center gap-2.5 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                            <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                              <Zap className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <div>
                              <p className="text-xs text-emerald-600/70 dark:text-emerald-400/70 font-medium">Online</p>
                              <p className="text-base font-bold text-emerald-600 dark:text-emerald-400">{formatDuration(workSessions.totals.online_seconds)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2.5 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                            <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                              <Pause className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            </div>
                            <div>
                              <p className="text-xs text-amber-600/70 dark:text-amber-400/70 font-medium">Idle</p>
                              <p className="text-base font-bold text-amber-600 dark:text-amber-400">{formatDuration(workSessions.totals.idle_seconds)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2.5 p-3 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                            <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                              <WifiOff className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                            </div>
                            <div>
                              <p className="text-xs text-slate-500/70 dark:text-slate-400/70 font-medium">Offline</p>
                              <p className="text-base font-bold text-slate-600 dark:text-slate-300">{formatDuration(workSessions.totals.offline_seconds)}</p>
                            </div>
                          </div>
                        </div>

                        {/* Sessions Table */}
                        <Table>
                          <TableHeader>
                            <TableRow className="border-slate-200 dark:border-slate-700">
                              <TableHead className="text-xs font-semibold uppercase tracking-wider w-28">Status</TableHead>
                              <TableHead className="text-xs font-semibold uppercase tracking-wider">Time Range</TableHead>
                              <TableHead className="text-xs font-semibold uppercase tracking-wider">Task / Project</TableHead>
                              <TableHead className="text-xs font-semibold uppercase tracking-wider text-center">Activity</TableHead>
                              <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Duration</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {workSessions.sessions.map((session, idx) => {
                              const sStart = new Date(session.start);
                              const sEnd = new Date(session.end);
                              const timeRange = `${sStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${sEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

                              const sessionStyle = {
                                online: {
                                  icon: <Zap className="w-3.5 h-3.5" />,
                                  label: 'Online',
                                  dot: 'bg-emerald-500',
                                  text: 'text-emerald-600 dark:text-emerald-400',
                                  bg: 'bg-emerald-50 dark:bg-emerald-900/20',
                                  rowBg: ''
                                },
                                idle: {
                                  icon: <Coffee className="w-3.5 h-3.5" />,
                                  label: 'Idle',
                                  dot: 'bg-amber-500',
                                  text: 'text-amber-600 dark:text-amber-400',
                                  bg: 'bg-amber-50 dark:bg-amber-900/20',
                                  rowBg: 'bg-amber-50/40 dark:bg-amber-950/10'
                                },
                                offline: {
                                  icon: <WifiOff className="w-3.5 h-3.5" />,
                                  label: 'Offline',
                                  dot: 'bg-slate-400',
                                  text: 'text-slate-500 dark:text-slate-400',
                                  bg: 'bg-slate-100 dark:bg-slate-800',
                                  rowBg: 'bg-slate-50/60 dark:bg-slate-900/20'
                                }
                              }[session.type] || { icon: null, label: session.type, dot: 'bg-slate-400', text: 'text-slate-500', bg: 'bg-slate-100', rowBg: '' };

                              const taskLabel = session.description || session.project_name || null;

                              return (
                                <TableRow key={idx} className={`border-slate-200 dark:border-slate-700 ${sessionStyle.rowBg}`} data-testid={`session-row-${idx}`}>
                                  <TableCell>
                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${sessionStyle.bg} ${sessionStyle.text}`}>
                                      <span className={`w-1.5 h-1.5 rounded-full ${sessionStyle.dot}`} />
                                      {sessionStyle.label}
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-sm font-mono text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                    {timeRange}
                                  </TableCell>
                                  <TableCell className="text-sm text-slate-700 dark:text-slate-300">
                                    {taskLabel ? (
                                      <span className="flex items-center gap-1.5">
                                        {session.project_name && (
                                          <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-medium">
                                            {session.project_name}
                                          </span>
                                        )}
                                        <span>{session.description || ''}</span>
                                      </span>
                                    ) : (
                                      <span className="text-slate-400 dark:text-slate-500 italic text-xs">
                                        {session.type === 'offline' ? 'Not tracking' : session.type === 'idle' ? 'No activity' : '—'}
                                      </span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    {session.activity_percent !== null && session.activity_percent !== undefined ? (
                                      <div className="flex items-center justify-center gap-2">
                                        <div className="w-16 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                          <div
                                            className={`h-full rounded-full ${session.activity_percent >= 70 ? 'bg-emerald-500' : session.activity_percent >= 40 ? 'bg-amber-500' : 'bg-rose-500'}`}
                                            style={{ width: `${Math.min(session.activity_percent, 100)}%` }}
                                          />
                                        </div>
                                        <span className={`text-xs font-semibold ${session.activity_percent >= 70 ? 'text-emerald-600 dark:text-emerald-400' : session.activity_percent >= 40 ? 'text-amber-600 dark:text-amber-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                          {session.activity_percent}%
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-xs text-slate-400">—</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <span className={`text-sm font-semibold ${sessionStyle.text}`}>
                                      {formatDuration(session.duration_seconds)}
                                    </span>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            {workSessions.sessions.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={5} className="text-center py-8 text-slate-400">
                                  No work sessions recorded
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-2">
                        <Layers className="w-8 h-8 opacity-50" />
                        <p className="text-sm">No session data available for this date</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
    </Layout>
  );
}

function getCategoryStyle(category) {
  return {
    productive: { dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20', label: 'Productive' },
    unproductive: { dot: 'bg-rose-500', text: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-900/20', label: 'Unproductive' },
    neutral: { dot: 'bg-slate-400', text: 'text-slate-500 dark:text-slate-400', bg: 'bg-slate-100 dark:bg-slate-800', label: 'Neutral' },
    uncategorized: { dot: 'bg-amber-400', text: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20', label: 'Uncategorized' }
  }[category] || { dot: 'bg-slate-400', text: 'text-slate-500', bg: 'bg-slate-100', label: category };
}

function UsageDetailRow({ item, idx }) {
  const catStyle = getCategoryStyle(item.category);
  const [expanded, setExpanded] = useState(false);
  const titles = Array.isArray(item.window_titles) ? item.window_titles : [];
  const hasTitles = titles.length > 0;

  return (
    <>
      <TableRow
        className={`border-slate-200 dark:border-slate-700 ${hasTitles ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50' : ''}`}
        onClick={() => hasTitles && setExpanded(v => !v)}
        data-testid={`detail-row-${idx}`}
      >
        <TableCell>
          <div className="flex items-center gap-1.5">
            {hasTitles ? (
              expanded
                ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
            ) : (
              <span className="w-3.5 h-3.5 inline-block" />
            )}
            {item.type === 'domain' ? (
              <Globe className="w-4 h-4 text-blue-500" />
            ) : (
              <Monitor className="w-4 h-4 text-purple-500" />
            )}
          </div>
        </TableCell>
        <TableCell className="font-medium text-slate-900 dark:text-slate-100">
          {item.name}
          {hasTitles && (
            <span className="ml-2 text-xs text-slate-400 dark:text-slate-500 font-normal">
              ({titles.length} {titles.length === 1 ? 'title' : 'titles'})
            </span>
          )}
        </TableCell>
        <TableCell className="text-center">
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${catStyle.bg} ${catStyle.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${catStyle.dot}`} />
            {catStyle.label}
          </span>
        </TableCell>
        <TableCell className="text-right text-sm font-medium text-slate-700 dark:text-slate-300">
          {formatDuration(item.duration_seconds)}
        </TableCell>
      </TableRow>
      {expanded && hasTitles && titles.map((t, ti) => (
        <TableRow
          key={`${idx}-title-${ti}`}
          className="border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/30"
          data-testid={`detail-row-${idx}-title-${ti}`}
        >
          <TableCell></TableCell>
          <TableCell className="text-sm text-slate-600 dark:text-slate-300 pl-8 truncate max-w-md" title={t.title}>
            <span className="text-slate-400 mr-2">↳</span>{t.title}
          </TableCell>
          <TableCell></TableCell>
          <TableCell className="text-right text-xs text-slate-500 dark:text-slate-400">
            {formatDuration(t.duration_seconds)}
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

function SummaryCard({ title, seconds, total, color, icon, isTotal }) {
  const pct = !isTotal && total > 0 ? Math.round((seconds / total) * 100) : null;
  const colorMap = {
    emerald: { bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200 dark:border-emerald-800', text: 'text-emerald-600 dark:text-emerald-400', bar: 'bg-emerald-500' },
    rose: { bg: 'bg-rose-50 dark:bg-rose-900/20', border: 'border-rose-200 dark:border-rose-800', text: 'text-rose-600 dark:text-rose-400', bar: 'bg-rose-500' },
    slate: { bg: 'bg-slate-100 dark:bg-slate-800', border: 'border-slate-200 dark:border-slate-700', text: 'text-slate-600 dark:text-slate-400', bar: 'bg-slate-400' },
    indigo: { bg: 'bg-indigo-50 dark:bg-indigo-900/20', border: 'border-indigo-200 dark:border-indigo-800', text: 'text-indigo-600 dark:text-indigo-400', bar: 'bg-indigo-500' },
    amber: { bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800', text: 'text-amber-600 dark:text-amber-400', bar: 'bg-amber-500' }
  };
  const c = colorMap[color];

  return (
    <Card className={`border ${c.border} ${c.bg}`} data-testid={`summary-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs font-semibold uppercase tracking-wider ${c.text}`}>{title}</span>
          <span className={c.text}>{icon}</span>
        </div>
        <p className={`text-2xl font-bold ${c.text}`}>{formatDuration(seconds)}</p>
        {pct !== null && (
          <div className="mt-2">
            <div className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${c.bar}`} style={{ width: `${Math.min(pct, 100)}%` }} />
            </div>
            <p className={`text-xs mt-1 ${c.text} opacity-70`}>{pct}% of total</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
